import { useSyncExternalStore } from 'react';
import { history, type HistoryEntry } from '../lib/history';
import { formatDuration, displayNumber } from '../lib/phone';
import { contacts, useContacts } from '../lib/contacts';
import { NgnBadge, useSheets } from './ContactSheets';
import { ArrowIn, ArrowOut, InfoIcon, PersonAddIcon, TrashIcon } from './Icons';

export function when(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === new Date(Date.now() - 86400000).toDateString()) return 'Yesterday';
  if (now.getTime() - d.getTime() < 6 * 86400000) return d.toLocaleDateString([], { weekday: 'long' });
  return d.toLocaleDateString([], { day: 'numeric', month: 'numeric', year: '2-digit' });
}

const LABEL: Record<HistoryEntry['status'], string> = {
  answered: '',
  missed: 'Missed',
  rejected: 'Declined',
  cancelled: 'Cancelled',
  failed: 'Not answered',
  transferred: 'Transferred',
};

export function Recents({ onCall }: { onCall: (n: string) => void }) {
  const entries = useSyncExternalStore(history.subscribe, history.getSnapshot);
  useContacts(); // re-render when names become known
  const { openEditor, openDetail } = useSheets();

  return (
    <div className="list">
      <div className="list-head">
        <h2 className="large-title">Recents</h2>
        {entries.length > 0 && (
          <button type="button" className="link" onClick={() => confirm('Clear all call history?') && history.clear()}>
            Clear
          </button>
        )}
      </div>
      {entries.length === 0 ? (
        <div className="empty">Your calls will appear here</div>
      ) : (
        <ul className="group" data-testid="history">
          {entries.map((e) => {
            const c = contacts.lookup(e.number);
            const title = c?.name ?? (e.name && e.name !== e.number ? e.name : displayNumber(e.number));
            const detail = LABEL[e.status] || (e.durationSec ? formatDuration(e.durationSec) : '');
            return (
              <li key={e.id} className={`row interactive ${e.status === 'missed' ? 'missed' : ''}`} data-status={e.status}>
                <span className="row-lead" title={e.direction}>
                  {e.direction === 'incoming' ? <ArrowIn /> : <ArrowOut />}
                </span>
                <button type="button" className="row-main" onClick={() => onCall(e.number)} title={`Call ${e.number}`}>
                  <span className="row-title">
                    <span className="name">{title}</span>
                    {c?.kind === 'customer' && <NgnBadge />}
                  </span>
                  <span className="row-sub">{[c || (e.name && e.name !== e.number) ? displayNumber(e.number) : '', detail].filter(Boolean).join(' · ')}</span>
                </button>
                <span className="row-trail">
                  <time>{when(e.startedAt)}</time>
                  <button type="button" className="icon-btn reveal muted" aria-label="Delete entry" onClick={() => history.remove(e.id)}>
                    <TrashIcon />
                  </button>
                  {c ? (
                    <button type="button" className="icon-btn" aria-label={`About ${c.name}`} onClick={() => openDetail(c)}>
                      <InfoIcon width={20} height={20} />
                    </button>
                  ) : (
                    <button type="button" className="icon-btn" aria-label="Add to contacts" data-testid="recents-add-contact" onClick={() => openEditor({ number: e.number, name: e.name && e.name !== e.number ? e.name : '' })}>
                      <PersonAddIcon width={20} height={20} />
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
